import React, { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import Lottie from "lottie-react";
import { toast } from "react-toastify";
import LeftArrow from "@svg/LeftArrow";
import ExpirationStatusText from "@elements/expiration-status-text";
import type { IRamadanRiddleUserReward } from "@types";
import confettiAnimationData from "assets/lottie/confetti.json";

interface RamadanRiddlesRewardViewProps {
    reward: IRamadanRiddleUserReward;
    onBack: () => void;
}

const RamadanRiddlesRewardView: React.FC<RamadanRiddlesRewardViewProps> = ({ reward, onBack }) => {
    const t = useTranslations();
    const { locale } = useRouter();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setCopied(false);
    }, [reward.id, reward.itemCode]);

    const handleCopyCode = async () => {
        if (!reward.itemCode) return;

        try {
            await navigator.clipboard.writeText(reward.itemCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch (error) {
            console.error("Failed to copy riddle reward code:", error);
            toast.error(t("client_general_error"));
        }
    };

    return (
        <div
            className="relative flex h-full flex-col bg-white text-[#38190D]"
            dir={locale?.includes("ar-") ? "rtl" : "ltr"}
        >
            {reward.show_confetti_celebration ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center">
                    <Lottie
                        animationData={confettiAnimationData}
                        loop={false}
                        autoPlay
                        className="w-full max-w-[520px]"
                    />
                </div>
            ) : null}

            <div className="flex items-center gap-2 px-6 pt-6 border-b border-[#EAEAEA] pb-6">
                <button
                    type="button"
                    onClick={onBack}
                    className="text-[#4F2F18] rtl:left-auto rtl:right-6"
                    aria-label={t("Back")}
                >
                    <LeftArrow className="rtl:rotate-180" />
                </button>
                <h2 className="text-[#4F2F18] font-extrabold text-xl uppercase">
                    {t("YOU UNLOCKED TODAY'S REWARD")}
                </h2>
            </div>

            <div className="flex-1 px-6 pb-10 pt-6">
                <h3 className="text-center text-[#7B5E4C] font-semibold mb-3">{reward.title}</h3>
                {reward.subTitle ? (
                    <p className="text-center text-[#7B5E4C] font-extrabold text-2xl mb-3 uppercase">
                        {reward.subTitle}
                    </p>
                ) : null}

                {reward.validUntil ? (
                    <div className="flex items-center justify-center gap-1 font-medium mb-6">
                        <span aria-hidden="true">⏰</span>
                        <ExpirationStatusText
                            className="text-center"
                            validUntil={reward.validUntil}
                        />
                    </div>
                ) : null}

                <div className="relative mb-10">
                    <div className="border-2 border-dashed border-[#593719] rounded-md px-8 py-4 text-center w-fit mx-auto">
                        <span className="font-bold text-[#593719] text-2xl tracking-widest">
                            {reward.itemCode}
                        </span>
                    </div>

                    {copied && (
                        <div className="absolute w-full text-center -bottom-7">
                            <span className="text-amber-500 font-medium">
                                🎉 {t("COUPON COPIED")}
                            </span>
                        </div>
                    )}
                </div>

                {reward.terms?.length ? (
                    <div className="mt-8 mb-10">
                        <h4 className="font-bold text-[#593719] mb-3 text-left rtl:text-right w-full">
                            {t("TERMS & CONDITIONS")}
                        </h4>
                        <ul className="space-y-2">
                            {reward.terms.map((term, index) => (
                                <li key={`${reward.id}-${index}`} className="flex">
                                    <span className="text-blue-500 mr-2">🔹</span>
                                    <span className="text-gray-700">{term}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                <button
                    type="button"
                    onClick={handleCopyCode}
                    className="bg-[#008EC6] text-white px-4 py-3 rounded-full text-sm font-bold transition hover:bg-[#007bb0] w-full"
                >
                    {t("COPY CODE")}
                </button>
            </div>
        </div>
    );
};

export default RamadanRiddlesRewardView;
